import { useEffect, useRef, useState } from "react";

import {
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import usePartMutations from "../../data/usePartMutations";
import { IPart } from "../../models/Part";
import classNames from "../../utils/classNames";

export const GRADE_STYLES: Record<string, string> = {
  A: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  B: "bg-sky-100 text-sky-800 ring-sky-200",
  C: "bg-amber-100 text-amber-800 ring-amber-200",
  D: "bg-orange-100 text-orange-800 ring-orange-200",
  U: "bg-red-100 text-red-800 ring-red-200",
};

export const GRADE_DEFINITIONS: {
  grade: string;
  title: string;
  description: string;
}[] = [
  {
    grade: "A",
    title: "Excellent",
    description:
      "Highest quality part. No, or minimal, damage, wear or corrosion. Fully functional, as new.",
  },
  {
    grade: "B",
    title: "Good",
    description:
      "Good condition. Small or moderate amount of damage or wear. Fully functional.",
  },
  {
    grade: "C",
    title: "Satisfactory",
    description:
      "Fair condition. Some wear or minor damage. Functional with possible cosmetic issues.",
  },
  {
    grade: "D",
    title: "Damaged",
    description:
      "Damaged part. Notable damage, wear or corrosion affecting condition. Functionality may be impaired.",
  },
  {
    grade: "U",
    title: "Not Tested/Out Of Scope",
    description:
      "Lowest quality part. Significant wear, damage or corrosion. May have functional issues.",
  },
];

interface PartGradeBadgeProps {
  part: IPart;
}

export default function PartGradeBadge({ part }: PartGradeBadgeProps) {
  const value = part.grade ?? "";
  const key = value.trim().toUpperCase().charAt(0);
  const badgeStyle =
    GRADE_STYLES[key] ?? "bg-slate-100 text-slate-700 ring-slate-200";

  const { patchPart } = usePartMutations();
  const [editing, setEditing] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;

    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setEditing(false);
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditing(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [editing]);

  const apply = (newGrade: string | null) => {
    if ((part.grade ?? null) === (newGrade ?? null)) {
      setEditing(false);
      return;
    }

    patchPart.mutate(
      { id: part.id, grade: newGrade, vehicleId: part.vehicleId },
      {
        onSettled: () => setEditing(false),
      }
    );
  };

  const trigger = value ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing((v) => !v);
      }}
      onMouseEnter={() => !editing && setHoverInfo(true)}
      onMouseLeave={() => setHoverInfo(false)}
      title="Click to change grade"
      aria-label={`Grade ${value}. Click to edit.`}
      className={classNames(
        "inline-flex h-6 min-w-[1.5rem] cursor-pointer items-center justify-center rounded-full px-2 text-xs font-semibold ring-1 ring-inset transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400",
        badgeStyle
      )}
    >
      {value}
    </button>
  ) : (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing((v) => !v);
      }}
      title="Grade missing - click to add"
      aria-label="Grade missing. Click to add."
      className="inline-flex whitespace-pre h-6 items-center gap-1 rounded-full bg-red-100 px-2 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200 transition-colors hover:bg-red-200 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-400"
    >
      <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>No grade</span>
    </button>
  );

  return (
    <div
      ref={ref}
      className="relative inline-block leading-none"
      onClick={(e) => e.stopPropagation()}
    >
      {trigger}

      {!editing && value && (
        <div
          className={classNames(
            "pointer-events-none absolute left-1/2 top-full z-40 mt-2 w-72 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl transition-all duration-150",
            hoverInfo ? "scale-100 opacity-100" : "scale-95 opacity-0"
          )}
          role="tooltip"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Grade Criteria
          </p>
          <ul className="space-y-2">
            {GRADE_DEFINITIONS.map((def) => {
              const defStyle =
                GRADE_STYLES[def.grade] ??
                "bg-slate-100 text-slate-700 ring-slate-200";
              const isCurrent = def.grade === key;

              return (
                <li
                  key={def.grade}
                  className={classNames(
                    "flex items-start gap-2 rounded-md p-1.5",
                    isCurrent ? "bg-slate-50 ring-1 ring-slate-200" : ""
                  )}
                >
                  <span
                    className={classNames(
                      "mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold ring-1 ring-inset",
                      defStyle
                    )}
                  >
                    {def.grade}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-800">
                      {def.title}
                    </span>
                    <span className="block text-xs leading-snug text-slate-600">
                      {def.description}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {editing && (
        <div
          role="dialog"
          aria-label="Set grade"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-1/2 top-full z-50 mt-2 w-56 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-xl"
        >
          <div className="mb-1 flex items-center justify-between px-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Set Grade
            </p>
            {value && (
              <button
                type="button"
                onClick={() => apply(null)}
                disabled={patchPart.isLoading}
                className="text-[11px] font-medium text-slate-500 hover:text-rose-600 disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <ul className="space-y-1">
            {GRADE_DEFINITIONS.map((def) => {
              const defStyle =
                GRADE_STYLES[def.grade] ??
                "bg-slate-100 text-slate-700 ring-slate-200";
              const isCurrent = def.grade === key;

              return (
                <li key={def.grade}>
                  <button
                    type="button"
                    onClick={() => apply(def.grade)}
                    disabled={patchPart.isLoading}
                    className={classNames(
                      "flex w-full items-center gap-2 rounded-md p-1.5 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50",
                      isCurrent ? "bg-slate-50 ring-1 ring-slate-200" : ""
                    )}
                  >
                    <span
                      className={classNames(
                        "inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset",
                        defStyle
                      )}
                    >
                      {def.grade}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-semibold text-slate-800">
                        {def.title}
                      </span>
                    </span>
                    {isCurrent && (
                      <CheckIcon
                        className="h-4 w-4 flex-none text-primary-600"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
